/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @next/next/no-img-element */
/* eslint-disable react-hooks/exhaustive-deps */
"use client";

import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FacebookIcon,
  LogOutIcon,
  MenuIcon,
} from "lucide-react";
import Image from "next/image";
import { Fragment, useEffect, useRef, useState } from "react";
import { useMeasure } from "react-use";
import { Autoplay, Navigation, Pagination as PaginationModule } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { Icons } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { User, useAuth } from "@/lib/auth";
import { axiosInstance } from "@/lib/axios";
import { EventType, Pagination } from "@/lib/types";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import Link from "next/link";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useBoundingclientrectRef } from "rooks";
import { AuthLink } from "@/components/auth-link";
import { NavLink } from "../(dashboard)/dashboard/nav-link";
import { useSidebarState } from "./sidebar";
import { SocialIcon } from "@/components/ui/social-icon";
import { TwitterIcon } from "lucide-react";
import { YoutubeIcon } from "lucide-react";
import { InstagramIcon } from "lucide-react";
import ThemeToggle from "@/components/ui/theme-toggle";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

export function OngoingEventsGrid() {
  const [ready, setReady] = useState(false);
  const [ref, rect] = useMeasure<HTMLDivElement>();

  let height = (660 / 940) * (rect.width ? rect.width : 1120);

  const prevRef = useRef(null);
  const nextRef = useRef(null);

  return (
    <div
      className="max-w-[1120px] w-full mx-auto pb-4 px-4 lg:px-0 relative"
      ref={ref}
    >
      <div
        className="grid grid-cols-12 gap-x-5"
        style={{
          height,
        }}
      >
        <div className="relative col-span-7 overflow-hidden rounded-xl bg-theme-gray">
          <Image
            src="/events/event-2.png"
            alt="Slider"
            fill
            style={{
              objectFit: "cover",
            }}
          />
        </div>

        <div className="relative col-span-5">
          <Swiper
            direction={"vertical"}
            spaceBetween={24}
            slidesPerView={1.5}
            modules={[Navigation]}
            style={{
              height,
            }}
            onAfterInit={() => {
              setReady(true);
            }}
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }}
            className="rounded-xl"
          >
            {["event-1", "event-2", "event-3", "event-4"].map((event, i) => {
              return (
                <SwiperSlide
                  key={i}
                  className={cn(
                    "relative overflow-hidden bg-red-100 rounded-xl",
                    ready ? "visible" : "invisible",
                  )}
                >
                  <Image
                    src={`/events/${event}.png`}
                    alt="Slider"
                    fill
                    style={{
                      objectFit: "cover",
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent"></div>
                </SwiperSlide>
              );
            })}
          </Swiper>
          <div
            className="absolute inset-x-0 z-10 flex justify-center -top-4"
            ref={prevRef}
          >
            <button className="p-2 rounded-full bg-theme-black/80">
              <ChevronUpIcon className="w-4 h-4 text-white" strokeWidth={2.5} />
            </button>
          </div>
          <div
            className="absolute inset-x-0 z-10 flex justify-center -bottom-4"
            ref={nextRef}
          >
            <button className="p-2 rounded-full bg-theme-black/80">
              <ChevronDownIcon
                className="w-4 h-4 text-white"
                strokeWidth={2.5}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const isMobile = () => window.innerWidth <= 1440;

export function LayoutSidebar({ children }: { children: React.ReactNode }) {
  const { isOpen, setIsOpen } = useSidebarState();

  useEffect(() => {
    // Detect resize event
    const resizeListener = () => {
      // Change width from the state object
      setIsOpen(!isMobile());
    };

    // Add resize listener
    window.addEventListener("resize", resizeListener);

    // Call resize listener right away
    resizeListener();

    // Remove resize listener on cleanup
    return () => window.removeEventListener("resize", resizeListener);
  }, []);

  return (
    <header className="flex shrink-0">
      <div className="hidden lg:block">
        <motion.div
          className="omega"
          initial={{
            width: 0,
          }}
          animate={{
            width: isOpen ? 320 : 120,
          }}
        ></motion.div>
      </div>
      <div className="fixed inset-y-0 left-0 z-20">
        <motion.div
          className="hidden h-full pt-[82px] max-h-screen bg-foreground dark:bg-[#DFE3E8] lg:overflow-y-auto border-zinc-900/10 lg:block"
          initial={{
            width: 0,
          }}
          animate={{
            width: isOpen ? 320 : 120,
          }}
          onClick={(e) => {
            // Close sidebar on mobile when clicking on a link
            if (isMobile()) {
              (e.target as Element).tagName === "A" && setIsOpen(false);
            }
          }}
        >
          <motion.div
            className="absolute top-[32px] left-[46px] z-20"
            animate={{
              x: isOpen ? 0 : 0,
              y: isOpen ? 0 : 0,
            }}
          >
            {/* <MenuIcons /> */}
          </motion.div>

          <div className="flex flex-col h-full overflow-hidden overflow-y-auto whitespace-nowrap">
            {children}
          </div>
        </motion.div>
      </div>
    </header>
  );
}

export function MenuIcons() {
  const { isOpen, setIsOpen } = useSidebarState();

  return (
    <button onClick={() => setIsOpen((open) => !open)}>
      {isOpen ? (
        <MenuIcon className="w-6 h-6 text-white dark:text-black" />
      ) : (
        <MenuIcon className="w-6 h-6 text-white dark:text-black" />
      )}
    </button>
  );
}

const SLIDERS = [
  {
    id: "1",
    title: "Broken Home",
    slug: "/events/broken-home.png",
    image: "/events/broken-home.png",
  },
  {
    id: "2",
    title: "Broken Home",
    slug: "/events/broken-home.png",
    image: "/events/broken-home.png",
  },
  {
    id: "3",
    title: "Broken Home",
    slug: "/events/broken-home.png",
    image: "/events/broken-home.png",
  },
  {
    id: "4",
    title: "Broken Home",
    slug: "/events/broken-home.png",
    image: "/events/broken-home.png",
  },
];

export function Sliders() {
  const [ready, setReady] = useState(false);

  return (
    <section className="relative px-4 py-8 lg:px-0">
      {!ready ? (
        <div className="max-w-[1120px] w-full bg-theme-gray rounded-xl animate-pulse aspect-[940/340] mx-auto absolute inset-0 mt-8" />
      ) : null}
      <Swiper
        slidesPerView={"auto"}
        centeredSlides={true}
        spaceBetween={24}
        pagination={{
          clickable: true,
        }}
        loop
        modules={[PaginationModule, Autoplay]}
        autoplay
        className="space-x-6"
        onAfterInit={() => {
          setReady(true);
        }}
      >
        {[...SLIDERS, ...SLIDERS].map((slider, i) => (
          <SwiperSlide key={i} className="max-w-[1120px] w-full">
            <Link href={`/${slider.slug}`}>
              <img
                src={`${slider.image}`}
                alt=""
                className={cn(
                  "object-cover w-full rounded-md",
                  ready ? "visible" : "invisible",
                )}
              />
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
}

export function CuratedEvents() {
  const scrollareaRefs = useRef<HTMLDivElement>(null);
  const [ref, rect] = useBoundingclientrectRef();

  function onScroll(direction: "next" | "prev") {
    let itemRect = rect;

    let width = itemRect?.width ?? 0;
    let scrollX = width + 16;

    scrollareaRefs.current?.scrollBy({
      left: direction === "next" ? scrollX : -scrollX,
      behavior: "smooth",
    });
  }

  const [selected, setSelected] = useState<EventType | null>(null);
  const [heightRef, { height }] = useMeasure<HTMLDivElement>();

  return (
    <section className="relative px-4 mb-4 pt-4 lg:pt-0 xl:px-0">
      {selected ? (
        <div className="max-w-[1120px] mx-auto mb-[80px] relative transition-all duration-200 ease-out">
          <motion.div
            animate={{ x: 0, height: height || "auto" }} // Memulai dari posisi x: 0 (tengah)
            initial={{ x: "100%", height: 0 }} // Memulai dari kanan (x: 100%)
            exit={{ x: "100%", height: 0 }} // Tambahkan jika Anda ingin animasi keluar ke kanan
            transition={{ duration: 0.5, ease: "easeOut" }} // Atur durasi dan easing
            className="transition-all duration-200 ease-out relative"
          >
            <div
              className="flex flex-col gap-4 pb-[14px] lg:pb-0 overflow-hidden md:flex-row relative"
              ref={heightRef}
            >
              <div className="rounded-2xl overflow-hidden w-full h-auto md:order-2 relative">
                {/* Gambar */}
                <img
                  src={selected.image_landscape ?? selected.image}
                  alt="Background"
                  className={`w-full rounded-2xl ${
                    !selected.image_landscape
                      ? "lg:h-[310px] h-[200px] object-cover"
                      : "h-auto"
                  }`}
                />
              </div>
              <div className="w-full py-4 space-y-2">
                <div className="text-xl dark:text-[#0D0D0D] font-extrabold md:text-4xl">
                  {selected.title}
                </div>
                {/* <div className='prose prose-invert dark:prose'> */}
                <div
                  className="text-sm font-semibold leading-relaxed text-transparent line-clamp-6 md:text-base dark:leading-relaxed dark:md:text-base dark:text-sm relative bg-gradient-to-b from-theme-gray via-theme-gray to-transparent bg-clip-text"
                  dangerouslySetInnerHTML={{
                    __html: selected.excerpt || selected.description,
                  }}
                ></div>
                {/* </div> */}
              </div>
              <Link
                href={`/${selected.slug}`}
                className="absolute bottom-0 left-0 z-[20] hover:bg-opacity-80 duration-200 inline-block bg-theme-accent py-[5px] lg:px-[10px] px-[10px] text-center lg:text-xs text-[10px] font-semibold text-white rounded-full"
              >
                Selengkapnya
              </Link>
            </div>
          </motion.div>
        </div>
      ) : null}

      <div className="relative">
        <TabEventsCarousel
          key="featured"
          type="featured"
          scrollAreaRef={scrollareaRefs}
          firstItemRef={ref}
          onEventClicked={(event) => setSelected(event)}
        />
      </div>
    </section>
  );
}

function TabEventsCarousel({
  type,
  scrollAreaRef,
  firstItemRef,
  onEventClicked,
}: {
  type: "featured" | "upcoming";
  scrollAreaRef: React.RefObject<HTMLDivElement>;
  onEventClicked?: (event: EventType) => void;
  firstItemRef:
    | (<T extends HTMLElement | null = HTMLElement>(node: T) => void)
    | null;
}) {
  const { isOpen, setIsOpen } = useSidebarState();

  const [zoomLevel, setZoomLevel] = useState<number>(100);
  useEffect(() => {
    const handleZoom = () => {
      setZoomLevel(window.devicePixelRatio * 100); // Menghitung persentase zoom
    };

    handleZoom();

    // Menambahkan event listener
    window.addEventListener("resize", handleZoom);

    // Membersihkan event listener
    return () => {
      window.removeEventListener("resize", handleZoom);
    };
  }, []);

  const { data, isLoading, isSuccess } = useQuery(
    ["home", "event-category", type],
    async () => {
      return axiosInstance
        .get(
          "/api/event-categories?" +
            new URLSearchParams({
              type,
            }).toString(),
        )
        .then((res) => res.data as Pagination<EventType>);
    },
  );

  if (isLoading) {
    return (
      <div className="max-w-[1120px] mx-auto pb-4 flex">
        <div className="grid grid-cols-3 gap-5">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="w-full bg-theme-gray rounded-xl animate-pulse aspect-[940/340]"
            />
          ))}
        </div>
      </div>
    );
  }

  if (!isSuccess || !data) {
    return null;
  }

  return (
    // <section className="w-full flex items-center justify-center relative">
    //   {/* <div className={`p-4 absolute text-theme-dark -top-[100px] left-0 z-[999] ${parseFloat(zoomLevel.toFixed(0)) >= 200 ? 'bg-pink-500' : 'bg-yellow-500'}`}>
    //     <h1 className="text-lg font-bold">Zoom Detection</h1>
    //     <p className={`mt-2`}>Current Zoom Level: {zoomLevel.toFixed(0)}%</p>
    //   </div> */}
    //   <div
    //     className={`hidden lg:block xl:block ${isOpen ? 'divSatuTrue' : 'divSatuFalse'} h-20 divSatu
    //       ${parseFloat(zoomLevel.toFixed(0)) === 50 ? 'zoomLevel50' : ''}
    //       ${parseFloat(zoomLevel.toFixed(0)) === 67 ? 'zoomLevel67' : ''}
    //       ${parseFloat(zoomLevel.toFixed(0)) === 100 ? 'zoomLevel100' : ''}
    //       ${parseFloat(zoomLevel.toFixed(0)) === 133 ? 'zoomLevel133' : ''}
    //       ${parseFloat(zoomLevel.toFixed(0)) === 150 ? 'zoomLevel150' : ''}
    //       ${parseFloat(zoomLevel.toFixed(0)) === 160 ? 'zoomLevel160' : ''}
    //       ${parseFloat(zoomLevel.toFixed(0)) === 180 ? 'zoomLevel180' : ''}`}
    //   ></div>
    //   <div
    //     className={`mb-4 ${isOpen ? 'divDuaTrue max-w-[1550px]' : 'divDuaFalse max-w-[1550px]'} transition-all duration-300 ease-in-out divDua
    //       ${parseFloat(zoomLevel.toFixed(0)) === 50 ? 'zoomLevel50' : ''}
    //       ${parseFloat(zoomLevel.toFixed(0)) === 67 ? 'zoomLevel67' : ''}
    //       ${parseFloat(zoomLevel.toFixed(0)) === 100 ? 'zoomLevel100' : ''}
    //       ${parseFloat(zoomLevel.toFixed(0)) === 133 ? 'zoomLevel133' : ''}
    //       ${parseFloat(zoomLevel.toFixed(0)) === 150 ? 'zoomLevel150' : ''}
    //       ${parseFloat(zoomLevel.toFixed(0)) === 160 ? 'zoomLevel160' : ''}
    //       ${parseFloat(zoomLevel.toFixed(0)) === 180 ? 'zoomLevel180' : ''}`}
    //   >
    //     <Swiper
    //       spaceBetween={14}
    //       slidesPerView={3.1}
    //       breakpoints={{
    //         320: { slidesPerView: 1.1, spaceBetween: 14 },
    //         640: { slidesPerView: 1.1, spaceBetween: 14 },
    //         768: { slidesPerView: 2.1, spaceBetween: 14 },
    //         1024: { slidesPerView: 3.1, spaceBetween: 14 },
    //       }}
    //     >
    //       {data.data.map((event) => (
    //         <SwiperSlide key={event.id} className="cursor-pointer max-w-[362px] max-h-[446px]">
    //           {/* <EventCard event={event} className="w-full" /> */}
    //           <div
    //             onClick={() => onEventClicked?.(event)}
    //             className={cn(
    //               "aspect-[260/320] relative w-[260px] rounded-xl overflow-hidden group block",
    //               "focus:ring-4 focus:ring-opacity-30 focus:ring-theme-accent outline-none",
    //               "w-full cursor-pointer"
    //             )}
    //           >
    //             <article className="absolute inset-0">
    //               <Image
    //                 className="transition-transform duration-300 group-hover:scale-110"
    //                 src={event.image}
    //                 alt="Slider"
    //                 fill
    //                 style={{
    //                   objectFit: "cover",
    //                 }}
    //               />
    //               <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent"></div>
    //             </article>

    //             <div className="absolute inset-x-0 bottom-0 p-4 transition-opacity duration-300 opacity-0 group-hover:opacity-100 bg-gradient-to-t via-white/50">
    //               <div className="text-lg font-bold">{event.title}</div>
    //               {/* Total View */}
    //               <div className="text-sm text-theme-gray">{event.total_page_views} views</div>
    //             </div>
    //           </div>
    //         </SwiperSlide>
    //       ))}
    //     </Swiper>
    //   </div>
    // </section>
    <section
      className={`w-full mx-auto max-w-[1250px] ${
        isOpen ? "pinTrue" : "pinFalse"
      } translate-x-0 grid grid-cols-12 relative`}
    >
      <div
        className={`mb-4 col-span-12 w-full transition-all duration-300 ease-in-out divDua`}
      >
        <Swiper
          spaceBetween={14}
          slidesPerView={3.1}
          breakpoints={{
            320: { slidesPerView: 1.1, spaceBetween: 14 },
            640: { slidesPerView: 1.1, spaceBetween: 14 },
            768: { slidesPerView: 2.1, spaceBetween: 14 },
            1024: { slidesPerView: 3.1, spaceBetween: 14 },
          }}
        >
          {data.data.map((event) => (
            <SwiperSlide
              key={event.id}
              className="cursor-pointer max-w-[362px] max-h-[446px]"
            >
              {/* <EventCard event={event} className="w-full" /> */}
              <div
                onClick={() => onEventClicked?.(event)}
                className={cn(
                  "aspect-[260/320] relative w-[260px] rounded-xl overflow-hidden group block",
                  "focus:ring-4 focus:ring-opacity-30 focus:ring-theme-accent outline-none",
                  "w-full cursor-pointer",
                )}
              >
                <article className="absolute inset-0">
                  <Image
                    className="transition-transform duration-300 group-hover:scale-110"
                    src={event.image}
                    alt="Slider"
                    fill
                    style={{
                      objectFit: "cover",
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent"></div>
                </article>

                <div className="absolute inset-x-0 bottom-0 p-4 transition-opacity duration-300 opacity-0 group-hover:opacity-100 bg-gradient-to-t via-white/50">
                  <div className="text-lg font-bold">{event.title}</div>
                  {/* Total View */}
                  <div className="text-sm text-theme-gray">
                    {event.total_page_views} views
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
}

export function CuratedEventsType() {
  const scrollareaRefs = useRef<HTMLDivElement>(null);
  const [ref, rect] = useBoundingclientrectRef();

  function onScroll(direction: "next" | "prev") {
    let itemRect = rect;

    let width = itemRect?.width ?? 0;
    let scrollX = width + 16;

    scrollareaRefs.current?.scrollBy({
      left: direction === "next" ? scrollX : -scrollX,
      behavior: "smooth",
    });
  }

  const [selected, setSelected] = useState<EventType | null>(null);
  const [heightRef, { height }] = useMeasure<HTMLDivElement>();

  return (
    <section className="relative pt-4">
      {selected ? (
        <div className="max-w-[520px] mx-auto mb-[80px] relative transition-all duration-200 ease-out">
          <motion.div
            animate={{ x: 0, height: height || "auto" }} // Memulai dari posisi x: 0 (tengah)
            initial={{ x: "100%", height: 0 }} // Memulai dari kanan (x: 100%)
            exit={{ x: "100%", height: 0 }} // Tambahkan jika Anda ingin animasi keluar ke kanan
            transition={{ duration: 0.5, ease: "easeOut" }} // Atur durasi dan easing
            className="transition-all duration-200 ease-out relative"
          >
            <div
              className="flex flex-col gap-4 pb-[14px] lg:pb-[14px] overflow-hidden relative"
              ref={heightRef}
            >
              <div className="rounded-2xl overflow-hidden w-full h-auto md:order-1 relative">
                {/* Gambar */}
                <img
                  src={selected.image_landscape ?? selected.image}
                  alt="Background"
                  className={`w-full rounded-2xl ${
                    !selected.image_landscape
                      ? "lg:h-[310px] h-[200px] object-cover"
                      : "h-auto"
                  }`}
                />
              </div>
              <div className="w-full py-4 space-y-2 md:order-2">
                <div className="text-xl dark:text-[#0D0D0D] font-extrabold md:text-4xl">
                  {selected.title}
                </div>
                {/* <div className='prose prose-invert dark:prose'> */}
                <div
                  className="text-sm font-semibold leading-relaxed text-transparent line-clamp-6 md:text-base dark:leading-relaxed dark:md:text-base dark:text-sm relative bg-gradient-to-b from-theme-gray via-theme-gray to-transparent bg-clip-text"
                  dangerouslySetInnerHTML={{
                    __html: selected.excerpt || selected.description,
                  }}
                ></div>
                {/* </div> */}
              </div>
              <Link
                href={`/${selected.slug}`}
                className="absolute bottom-0 left-0 z-[20] hover:bg-opacity-80 duration-200 inline-block bg-theme-accent py-[5px] lg:px-[10px] px-[10px] text-center lg:text-xs text-[10px] font-semibold text-white rounded-full"
              >
                Selengkapnya
              </Link>
            </div>
          </motion.div>
        </div>
      ) : null}

      <div className="relative">
        <TabEventsCarouselType
          key="featured"
          type="featured"
          scrollAreaRef={scrollareaRefs}
          firstItemRef={ref}
          onEventClicked={(event) => setSelected(event)}
        />
      </div>
    </section>
  );
}

function TabEventsCarouselType({
  type,
  scrollAreaRef,
  firstItemRef,
  onEventClicked,
}: {
  type: "featured" | "upcoming";
  scrollAreaRef: React.RefObject<HTMLDivElement>;
  onEventClicked?: (event: EventType) => void;
  firstItemRef:
    | (<T extends HTMLElement | null = HTMLElement>(node: T) => void)
    | null;
}) {
  const { isOpen, setIsOpen } = useSidebarState();

  const [zoomLevel, setZoomLevel] = useState<number>(100);
  useEffect(() => {
    const handleZoom = () => {
      setZoomLevel(window.devicePixelRatio * 100); // Menghitung persentase zoom
    };

    handleZoom();

    // Menambahkan event listener
    window.addEventListener("resize", handleZoom);

    // Membersihkan event listener
    return () => {
      window.removeEventListener("resize", handleZoom);
    };
  }, []);

  const { data, isLoading, isSuccess } = useQuery(
    ["home", "event-category", type],
    async () => {
      return axiosInstance
        .get(
          "/api/event-categories?" +
            new URLSearchParams({
              type,
            }).toString(),
        )
        .then((res) => res.data as Pagination<EventType>);
    },
  );

  if (isLoading) {
    return (
      <div className="max-w-[520px] mx-auto pb-4 flex">
        <div className="grid grid-cols-1 gap-5">
          {Array.from({ length: 1 }).map((_, i) => (
            <div
              key={i}
              className="w-full md:h-[500px] h-[250px] bg-theme-gray rounded-xl animate-pulse aspect-[940/340]"
            />
          ))}
        </div>
      </div>
    );
  }

  if (!isSuccess || !data) {
    return null;
  }

  return (
    <>
      <section className={`w-full grid grid-cols-1 relative overflow-hidden`}>
        {/* OPACITY LEFT */}
        <div
          className="pointer-events-none absolute left-0 top-0 h-full lg:w-[0px] md:w-[50px] w-[16px] dark:lg:via-[#FFFFFF] lg:via-[#1B1E23] 
        bg-gradient-to-r dark:from-[#FFFFFF] from-[#1B1E23] to-transparent z-10"
        />
        {/* OPACITY RIGHT */}
        <div
          className="pointer-events-none absolute right-0 top-0 h-full lg:w-[800px] md:w-[100px] w-[16px] dark:lg:via-[#FFFFFF] lg:via-[#1B1E23] 
        bg-gradient-to-l dark:from-[#FFFFFF] from-[#1B1E23] to-transparent z-10"
        />

        {/* SWIPER */}
        <div
          className={`mb-4 col-span-1 w-full md:max-w-[520px] max-w-none md:mx-auto transition-all duration-300 ease-in-out px-4 md:px-0 lg:px-0`}
        >
          <Swiper
            spaceBetween={14}
            slidesPerView={3.1}
            breakpoints={{
              320: { slidesPerView: 1.01, spaceBetween: 10 },
              640: { slidesPerView: 1, spaceBetween: 10 },
              768: { slidesPerView: 1, spaceBetween: 10 },
              1024: { slidesPerView: 1, spaceBetween: 14 },
            }}
            className="curated-events-type-swiper"
          >
            {data.data.map((event) => (
              <SwiperSlide key={event.id} className="cursor-pointer w-full">
                {/* <EventCard event={event} className="w-full" /> */}
                <Link
                  href={"/" + event.slug}
                  className={cn(
                    "aspect-[260/320] relative w-[260px] rounded-xl overflow-hidden group block",
                    "focus:ring-4 focus:ring-opacity-30 focus:ring-theme-accent outline-none",
                    "w-full cursor-pointer",
                  )}
                >
                  <article className="absolute inset-0">
                    <Image
                      className="transition-transform duration-300 group-hover:scale-110"
                      src={event.image}
                      alt="Slider"
                      fill
                      style={{
                        objectFit: "cover",
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent"></div>
                  </article>

                  <div className="absolute inset-x-0 bottom-0 p-4 transition-opacity duration-300 opacity-0 group-hover:opacity-100 bg-gradient-to-t via-white/50">
                    <div className="text-lg font-bold">{event.title}</div>
                    {/* Total View */}
                    <div className="text-sm text-theme-gray">
                      {event.total_page_views} views
                    </div>
                  </div>
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>
    </>
  );
}

export function PastEvents() {
  const { data, isLoading, isSuccess, fetchNextPage, hasNextPage } =
    useInfiniteQuery(
      ["home", "past-events"],
      async ({ pageParam = 1 }) => {
        return axiosInstance
          .get(`/api/event-categories?page=${pageParam}`)
          .then((res) => res.data as Pagination<EventType>);
      },
      {
        getNextPageParam: (lastPage) => {
          return lastPage.next_page_url ? lastPage.current_page + 1 : undefined;
        },
      },
    );

  const [sentryRef] = useInfiniteScroll({
    hasNextPage: Boolean(hasNextPage),
    loading: isLoading,
    onLoadMore: fetchNextPage,
  });

  if (isLoading) {
    <div className="max-w-[1120px] mx-auto pb-4 flex">
      <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="w-full bg-theme-gray rounded-xl animate-pulse aspect-[940/340]"
          />
        ))}
      </div>
    </div>;
  }

  if (!isSuccess || !data) {
    return null;
  }

  return (
    <div className="px-4 pb-4 pt-0 xl:px-0">
      <section className="max-w-[1120px] mx-auto pb-4">
        <div className="grid grid-cols-2 gap-4 overflow-x-auto lg:grid-cols-3">
          {data.pages.map((page, i) => (
            <Fragment key={i}>
              {page.data.map((event) => (
                <EventCard key={event.id} event={event} className="w-full" />
              ))}
            </Fragment>
          ))}
        </div>
        {hasNextPage && (
          <Icons.spinner
            ref={sentryRef}
            className={cn("w-6 h-6 mx-auto animate-spin", {
              "opacity-0": isLoading,
            })}
          />
        )}
      </section>
    </div>
  );
}

export function EventCard({
  event,
  className,
}: {
  event: EventType;
  className?: string;
}) {
  return (
    <Link
      href={`/${event.slug}`}
      className={cn(
        "aspect-[260/320] relative w-[260px] rounded-xl overflow-hidden group block",
        "focus:ring-4 focus:ring-opacity-30 focus:ring-theme-accent outline-none",
        className,
      )}
    >
      <article className="absolute inset-0">
        <Image
          className="transition-transform duration-300 group-hover:scale-110"
          src={event.image}
          alt="Slider"
          fill
          style={{
            objectFit: "cover",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent"></div>
      </article>

      <div className="absolute inset-x-0 bottom-0 p-4 transition-opacity duration-300 opacity-0 group-hover:opacity-100 bg-gradient-to-t via-white/50">
        <div className="text-lg font-bold">{event.title}</div>
        {/* Total View */}
        <div className="text-sm text-theme-gray">
          {event.total_page_views} views
        </div>
      </div>
    </Link>
  );
}

export function UserDropdown({ initialData }: { initialData: User | null }) {
  const auth = useAuth({
    initialData,
  });

  if (auth.status === "authenticated") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div className="flex items-center shrink-0 pr-4 lg:pr-0">
            <img
              src={auth.user.profile_photo_url}
              alt="Profile photo"
              className="w-6 h-6 border-white rounded-full border-px"
            />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[350px]" align="end">
          <DropdownMenuItem asChild>
            <Link
              href="/dashboard/ticket"
              className="w-full hover space-x-2.5 text-sm xl:text-base"
            >
              <Icons.ticket className="w-4 h-4 text-theme-accent" />

              <span>Tiket Saya</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              href="/dashboard/account"
              className="w-full hover space-x-2.5 text-sm xl:text-base"
            >
              <Icons.account className="w-4 h-4 text-theme-accent" />

              <span>Informasi Akun</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              href="/spinner"
              className="w-full hover space-x-2.5 text-sm xl:text-base"
            >
              <Icons.spinn className="w-4 h-4 text-theme-accent" />

              <span>Spinner</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <button
              onClick={auth.logout}
              className="w-full hover space-x-2.5 text-sm xl:text-base"
            >
              <LogOutIcon className="w-4 h-4 text-theme-accent" />

              <span>Logout</span>
            </button>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div className="flex shrink-0">
      <Button
        variant="link"
        className="hidden h-10 px-4 text-sm text-white dark:text-[#0D0D0D] lg:px-6 lg:text-lg lg:block"
        asChild
      >
        <AuthLink to="/sign-in">Masuk</AuthLink>
      </Button>
      <Button
        className="hidden h-10 px-6 dark:bg-theme-accent text-lg lg:block dark:text-white"
        asChild
      >
        <AuthLink to="/sign-up">Daftar</AuthLink>
      </Button>
    </div>
  );
}

export function LeftSidebarMenus() {
  const { isOpen, setIsOpen } = useSidebarState();

  const auth = useAuth();

  if (auth.status === "authenticated") {
    return (
      <>
        {/* <Link href="/" className={`${isOpen ? 'opacity-100 pb-8 pt-5' : 'opacity-0 py-8'} `}>
          <Image
            src="/logo.png"
            className="object-contain mx-auto"
            width={98}
            height={44}
            alt="Mastermind logo"
          />
        </Link> */}
        <div className="space-y-5 pt-[48px]">
          <NavLink
            className={`${
              isOpen
                ? "flex items-center"
                : "flex flex-col items-center justify-center py-[10px] px-[10px]"
            }`}
            href="/dashboard"
            index
          >
            <Icons.allEventsNew
              className={`${isOpen ? "w-4 h-4" : "w-6 h-6"}  shrink-0`}
            />
            <span
              className={`${
                isOpen ? "block" : "hidden"
              } ml-2.5 font-medium text-white dark:text-[#0D0D0D] text-sm xl:text-base`}
            >
              Dashboard
            </span>
            {/* <span className={`${isOpen ? "hidden" : "block"} mt-[10px] font-medium text-white dark:text-[#0D0D0D] text-[10px] xl:text-[10px] text-center`}>
              All
            </span> */}
          </NavLink>
          <NavLink
            className={`${
              isOpen
                ? "flex items-center"
                : "flex flex-col items-center justify-center py-[10px] px-[10px]"
            }`}
            href="/events/all"
          >
            <Icons.star
              className={`${isOpen ? "w-4 h-4" : "w-6 h-6"}  shrink-0`}
            />
            <span
              className={`${
                isOpen ? "block" : "hidden"
              } ml-2.5 font-medium text-white dark:text-[#0D0D0D] text-sm xl:text-base`}
            >
              Explore Event
            </span>
            {/* <span className={`${isOpen ? "hidden" : "block"} mt-[10px] font-medium text-white dark:text-[#0D0D0D] text-[10px] xl:text-[10px] text-center`}>
              Explore
            </span> */}
          </NavLink>
          <NavLink
            className={`${
              isOpen
                ? "flex items-center"
                : "flex flex-col items-center justify-center py-[10px] px-[10px]"
            }`}
            href="/dashboard/ticket"
          >
            <Icons.ticket
              className={`${isOpen ? "w-4 h-4" : "w-6 h-6"}  shrink-0`}
            />
            <span
              className={`${
                isOpen ? "block" : "hidden"
              } ml-2.5 font-medium text-white dark:text-[#0D0D0D] text-sm xl:text-base`}
            >
              Tiket Saya
            </span>
            {/* <span className={`${isOpen ? "hidden" : "block"} mt-[10px] font-medium text-white dark:text-[#0D0D0D] text-[10px] xl:text-[10px] text-center`}>
              Ticket
            </span> */}
          </NavLink>

          <div
            className={`${
              isOpen ? "text-sm" : "text-xs"
            } font-semibold text-theme-gray`}
          >
            Akun saya
          </div>

          <NavLink
            className={`${
              isOpen
                ? "flex items-center"
                : "flex flex-col items-center justify-center py-[10px] px-[10px]"
            }`}
            href="/dashboard/affiliate"
          >
            <Icons.megaphone
              className={`${isOpen ? "w-4 h-4" : "w-6 h-6"}  shrink-0`}
            />
            <span
              className={`${
                isOpen ? "block" : "hidden"
              } ml-2.5 font-medium text-white dark:text-[#0D0D0D] text-sm xl:text-base`}
            >
              Affiliate
            </span>
            {/* <span className={`${isOpen ? "hidden" : "block"} mt-[10px] font-medium text-white dark:text-[#0D0D0D] text-[10px] xl:text-[10px] text-center`}>
              Affiliate
            </span> */}
          </NavLink>
          <NavLink
            className={`${
              isOpen
                ? "flex items-center"
                : "flex flex-col items-center justify-center py-[10px] px-[10px]"
            }`}
            href="/dashboard/account"
          >
            <Icons.account
              className={`${isOpen ? "w-4 h-4" : "w-6 h-6"}  shrink-0`}
            />
            <span
              className={`${
                isOpen ? "block" : "hidden"
              } ml-2.5 font-medium text-white dark:text-[#0D0D0D] text-sm xl:text-base`}
            >
              Informasi Akun
            </span>
            {/* <span className={`${isOpen ? "hidden" : "block"} mt-[10px] font-medium text-white dark:text-[#0D0D0D] text-[10px] xl:text-[10px] text-center`}>
              Akun
            </span> */}
          </NavLink>
          <NavLink
            className={`${
              isOpen
                ? "flex items-center"
                : "flex flex-col items-center justify-center py-[10px] px-[10px]"
            }`}
            href={`${process.env.NEXT_PUBLIC_DASHBOARD_BASEURL}/authenticate`}
            target="_blank"
          >
            <Icons.calendar
              className={`${isOpen ? "w-4 h-4" : "w-6 h-6"}  shrink-0`}
            />
            <span
              className={`${
                isOpen ? "block" : "hidden"
              } ml-2.5 font-medium text-white dark:text-[#0D0D0D] text-sm xl:text-base`}
            >
              Event creator
            </span>
            {/* <span className={`${isOpen ? "hidden" : "block"} mt-[10px] font-medium text-white dark:text-[#0D0D0D] text-[10px] xl:text-[10px] text-center`}>
              Event
            </span> */}
          </NavLink>
          <NavLink
            className={`${
              isOpen
                ? "flex items-center"
                : "flex flex-col items-center justify-center py-[10px] px-[10px]"
            }`}
            href="/spinner"
          >
            <Icons.spinn
              className={`${isOpen ? "w-4 h-4" : "w-6 h-6"}  shrink-0`}
            />
            <span
              className={`${
                isOpen ? "block" : "hidden"
              } ml-2.5 font-medium text-white dark:text-[#0D0D0D] text-sm xl:text-base`}
            >
              Spinner
            </span>
            {/* <span className={`${isOpen ? "hidden" : "block"} mt-[10px] font-medium text-white dark:text-[#0D0D0D] text-[10px] xl:text-[10px] text-center`}>
              Spinner
            </span> */}
          </NavLink>
          <LogoutMenuButton />
        </div>
        <div className="w-full h-full max-h-screen flex flex-col justify-end items-start">
          <div
            className={`${
              isOpen ? "block" : "hidden"
            } space-y-3 text-[10px] font-semibold xl:text-sm text-theme-gray`}
          >
            <Link href="/payment-confirm" className="block">
              Konfirmasi Pembayaran
            </Link>
            <Link href="/page/about" className="block">
              Tentang MastermindEO
            </Link>
            <Link href="/page/privacy-policy" className="block">
              Kebijakan Privasi
            </Link>
            <Link href="/page/terms-and-condition" className="block">
              Syarat & Ketentuan
            </Link>
            <Link href="/page/faq" className="block">
              FAQ
            </Link>
          </div>
          <div
            className={`${
              isOpen ? "flex flex-col" : "hidden"
            } pt-4 text-[10px] font-medium text-theme-gray`}
          >
            <p>© Copyright MastermindEO®</p>
            <p>All Rights Reserved.</p>
          </div>
          <div
            className={`${
              isOpen
                ? "flex py-8 space-x-5"
                : "flex flex-col items-center justify-center py-8 space-y-5"
            } w-full`}
          >
            <SocialIcon
              icon={<FacebookIcon />}
              href="https://www.facebook.com/mastermindeo"
            />
            <SocialIcon
              icon={<TwitterIcon />}
              href="https://www.facebook.com/mastermindeo"
            />
            <SocialIcon
              icon={<YoutubeIcon />}
              href="https://www.facebook.com/mastermindeo"
            />
            <SocialIcon
              icon={<InstagramIcon />}
              href="https://www.facebook.com/mastermindeo"
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* <Link href="/" className={`${isOpen ? 'opacity-100 pb-8 pt-5' : 'opacity-0 py-8'} `}>
        <Image
          src="/logo.png"
          className="object-contain mx-auto"
          width={98}
          height={44}
          alt="Mastermind logo"
        />
      </Link> */}
      <div className="space-y-5 pt-[48px]">
        <NavLink
          className={`${
            isOpen
              ? "flex items-center"
              : "flex flex-col items-center justify-center py-[10px] px-[10px]"
          }`}
          href="/events/all"
          index
        >
          <Icons.allEventsNew
            className={`${isOpen ? "w-4 h-4" : "w-6 h-6"}  shrink-0`}
          />
          <span
            className={`${
              isOpen ? "block" : "hidden"
            } ml-2.5 font-medium text-white dark:text-[#0D0D0D] text-sm xl:text-base`}
          >
            Semua Event
          </span>
          {/* <span className={`${isOpen ? "hidden" : "block"} mt-[10px] font-medium text-white dark:text-[#0D0D0D] text-[10px] xl:text-[10px] text-center`}>
            All
          </span> */}
        </NavLink>

        <NavLink
          className={`${
            isOpen
              ? "flex items-center"
              : "flex flex-col items-center justify-center py-[10px] px-[10px]"
          }`}
          href="/events/featured"
        >
          <Icons.star
            className={`${isOpen ? "w-4 h-4" : "w-6 h-6"}  shrink-0`}
          />
          <span
            className={`${
              isOpen ? "block" : "hidden"
            } ml-2.5 font-medium text-white dark:text-[#0D0D0D] text-sm xl:text-base`}
          >
            Event Pilihan
          </span>
          {/* <span className={`${isOpen ? "hidden" : "block"} mt-[10px] font-medium text-white dark:text-[#0D0D0D] text-[10px] xl:text-[10px] text-center`}>
            Pinned
          </span> */}
        </NavLink>

        <NavLink
          className={`${
            isOpen
              ? "flex items-center"
              : "flex flex-col items-center justify-center py-[10px] px-[10px]"
          }`}
          href="/events/upcoming"
        >
          <Icons.ticket
            className={`${isOpen ? "w-4 h-4" : "w-6 h-6"}  shrink-0`}
          />
          <span
            className={`${
              isOpen ? "block" : "hidden"
            } ml-2.5 font-medium text-white dark:text-[#0D0D0D] text-sm xl:text-base`}
          >
            Event Selanjutnya
          </span>
          {/* <span className={`${isOpen ? "hidden" : "block"} mt-[10px] font-medium text-white dark:text-[#0D0D0D] text-[10px] xl:text-[10px] text-center`}>
            Next
          </span> */}
        </NavLink>

        <NavLink
          className={`${
            isOpen
              ? "flex items-center"
              : "flex flex-col items-center justify-center py-[10px] px-[10px]"
          }`}
          href="/events/past"
        >
          <Icons.past
            className={`${isOpen ? "w-4 h-4" : "w-6 h-6"}  shrink-0`}
          />
          <span
            className={`${
              isOpen ? "block" : "hidden"
            } ml-2.5 font-medium text-white dark:text-[#0D0D0D] text-sm xl:text-base`}
          >
            Event Berlalu
          </span>
          {/* <span className={`${isOpen ? "hidden" : "block"} mt-[10px] font-medium text-white dark:text-[#0D0D0D] text-[10px] xl:text-[10px] text-center`}>
            Past
          </span> */}
        </NavLink>

        <div
          className={`${
            isOpen ? "block" : "hidden"
          } pb-2 break-words text-theme-gray`}
        >
          Masuk untuk informasi
          <br /> lebih lengkap
        </div>
        <Button
          className="block w-full text-base text-center hover:text-white text-theme-accent border-theme-accent hover:bg-theme-accent dark:hover:bg-theme-accent dark:hover:border-theme-accent dark:hover:text-white lg:hidden"
          variant="outline"
          asChild
        >
          <AuthLink to="/sign-in">Masuk</AuthLink>
        </Button>
        <Button
          className={`hidden w-full text-base hover:text-white text-theme-accent border-theme-accent hover:bg-theme-accent dark:hover:bg-theme-accent dark:hover:border-theme-accent dark:hover:text-white ${
            isOpen ? "lg:block" : "lg:hidden"
          }`}
          variant="outline"
          asChild
        >
          <AuthLink to="/sign-in">Masuk</AuthLink>
        </Button>
      </div>
      <div className="w-full h-full max-h-screen flex flex-col justify-end items-start">
        <div
          className={`${
            isOpen ? "block" : "hidden"
          } space-y-3 text-[10px] font-semibold xl:text-sm text-theme-gray`}
        >
          <Link href="/payment-confirm" className="block">
            Konfirmasi Pembayaran
          </Link>
          <Link href="/page/about" className="block">
            Tentang MastermindEO
          </Link>
          <Link href="/page/privacy-policy" className="block">
            Kebijakan Privasi
          </Link>
          <Link href="/page/terms-and-condition" className="block">
            Syarat & Ketentuan
          </Link>
          <Link href="/page/faq" className="block">
            FAQ
          </Link>
        </div>
        <div
          className={`${
            isOpen ? "flex flex-col" : "hidden"
          } pt-4 text-[10px] font-medium text-theme-gray`}
        >
          <p>© Copyright MastermindEO®</p>
          <p>All Rights Reserved.</p>
        </div>
        <div
          className={`${
            isOpen
              ? "flex py-8 space-x-5"
              : "flex flex-col items-center justify-center py-8 space-y-5"
          } w-full`}
        >
          <SocialIcon
            icon={<FacebookIcon />}
            href="https://www.facebook.com/mastermindeo"
          />
          <SocialIcon
            icon={<TwitterIcon />}
            href="https://www.facebook.com/mastermindeo"
          />
          <SocialIcon
            icon={<YoutubeIcon />}
            href="https://www.facebook.com/mastermindeo"
          />
          <SocialIcon
            icon={<InstagramIcon />}
            href="https://www.facebook.com/mastermindeo"
          />
        </div>
      </div>
    </>
  );
}

export function LeftSidebarMenusSpinner() {
  const { isOpen, setIsOpen } = useSidebarState();

  const auth = useAuth();

  if (auth.status === "authenticated") {
    return (
      <>
        {/* <Link href="/" className={`fixed top-4 left-[120px] opacity-100`}>
          <Image
            src="/logo.png"
            className="object-contain mx-auto"
            width={98}
            height={44}
            alt="Mastermind logo"
          />
        </Link> */}
        <div className="space-y-5 pt-[48px]">
          <NavLink
            className={`${
              isOpen
                ? "flex items-center"
                : "flex flex-col items-center justify-center py-[10px] px-[10px]"
            }`}
            href="/spinner"
            index
          >
            <Icons.spinn
              className={`${isOpen ? "w-4 h-4" : "w-6 h-6"}  shrink-0`}
            />
            <span
              className={`${
                isOpen ? "block" : "hidden"
              } ml-2.5 font-medium text-white dark:text-[#0D0D0D] text-sm xl:text-base`}
            >
              Spinner
            </span>
            {/* <span className={`${isOpen ? "hidden" : "block"} mt-[10px] font-medium text-white dark:text-[#0D0D0D] text-[10px] xl:text-[10px] text-center`}>
              Spinner
            </span> */}
          </NavLink>
          <NavLink
            className={`${
              isOpen
                ? "flex items-center"
                : "flex flex-col items-center justify-center py-[10px] px-[10px]"
            }`}
            href="/spinner/winners"
          >
            <Icons.star
              className={`${isOpen ? "w-4 h-4" : "w-6 h-6"}  shrink-0`}
            />
            <span
              className={`${
                isOpen ? "block" : "hidden"
              } ml-2.5 font-medium text-white dark:text-[#0D0D0D] text-sm xl:text-base`}
            >
              Winner
            </span>
            {/* <span className={`${isOpen ? "hidden" : "block"} mt-[10px] font-medium text-white dark:text-[#0D0D0D] text-[10px] xl:text-[10px] text-center`}>
              Winner
            </span> */}
          </NavLink>
        </div>
      </>
    );
  } else {
    return (
      <>
        {/* <Link href="/" className={`${isOpen ? 'opacity-100 pb-8 pt-5' : 'opacity-0 py-8'} `}>
          <Image
            src="/logo.png"
            className="object-contain mx-auto"
            width={98}
            height={44}
            alt="Mastermind logo"
          />
        </Link> */}
        <div className="space-y-5 pt-[48px]">
          <NavLink
            className={`${
              isOpen
                ? "flex items-center"
                : "flex flex-col items-center justify-center py-[10px] px-[10px]"
            }`}
            href="/spinner"
            index
          >
            <Icons.spinn
              className={`${isOpen ? "w-4 h-4" : "w-6 h-6"}  shrink-0`}
            />
            <span
              className={`${
                isOpen ? "block" : "hidden"
              } ml-2.5 font-medium text-white text-sm xl:text-base`}
            >
              Spinner
            </span>
            {/* <span className={`${isOpen ? "hidden" : "block"} mt-[10px] font-medium text-white text-[10px] xl:text-[10px] text-center`}>
              Spinner
            </span> */}
          </NavLink>
          <NavLink
            className={`${
              isOpen
                ? "flex items-center"
                : "flex flex-col items-center justify-center py-[10px] px-[10px]"
            }`}
            href="/spinner/winners"
          >
            <Icons.star
              className={`${isOpen ? "w-4 h-4" : "w-6 h-6"}  shrink-0`}
            />
            <span
              className={`${
                isOpen ? "block" : "hidden"
              } ml-2.5 font-medium text-white text-sm xl:text-base`}
            >
              Winner
            </span>
            {/* <span className={`${isOpen ? "hidden" : "block"} mt-[10px] font-medium text-white text-[10px] xl:text-[10px] text-center`}>
              Winner
            </span> */}
          </NavLink>
        </div>
      </>
    );
  }

  // return (
  //   <>
  //     <Link href="/" className={`${isOpen ? 'opacity-100 py-8' : 'opacity-0 py-8'} `}>
  //       <Image
  //         src="/logo.png"
  //         className="object-contain mx-auto"
  //         width={98}
  //         height={44}
  //         alt="Mastermind logo"
  //       />
  //     </Link>
  //     <NavLink className={`${isOpen ? "flex items-center" : "flex flex-col items-center justify-center py-[10px] px-[10px]"}`} href="/events/all" index>
  //       <Icons.allEvents className={`${isOpen ? "w-4 h-4" : "w-6 h-6"}  shrink-0`} />
  //       <span className={`${isOpen ? "block" : "hidden"} ml-2.5 font-medium text-white text-sm xl:text-base`}>
  //         Semua Event
  //       </span>
  //       <span className={`${isOpen ? "hidden" : "block"} mt-[10px] font-medium text-white text-[10px] xl:text-[10px] text-center`}>
  //         All
  //       </span>
  //     </NavLink>

  //     <NavLink className={`${isOpen ? "flex items-center" : "flex flex-col items-center justify-center py-[10px] px-[10px]"}`} href="/events/curated">
  //       <Icons.star className={`${isOpen ? "w-4 h-4" : "w-6 h-6"}  shrink-0`} />
  //       <span className={`${isOpen ? "block" : "hidden"} ml-2.5 font-medium text-white text-sm xl:text-base`}>
  //         Event Pilihan
  //       </span>
  //       <span className={`${isOpen ? "hidden" : "block"} mt-[10px] font-medium text-white text-[10px] xl:text-[10px] text-center`}>
  //         Pinned
  //       </span>
  //     </NavLink>

  //     <NavLink className={`${isOpen ? "flex items-center" : "flex flex-col items-center justify-center py-[10px] px-[10px]"}`} href="/events/upcoming">
  //       <Icons.ticket className={`${isOpen ? "w-4 h-4" : "w-6 h-6"}  shrink-0`} />
  //       <span className={`${isOpen ? "block" : "hidden"} ml-2.5 font-medium text-white text-sm xl:text-base`}>
  //         Event Akan Datang
  //       </span>
  //       <span className={`${isOpen ? "hidden" : "block"} mt-[10px] font-medium text-white text-[10px] xl:text-[10px] text-center`}>
  //         Next
  //       </span>
  //     </NavLink>

  //     <NavLink className={`${isOpen ? "flex items-center" : "flex flex-col items-center justify-center py-[10px] px-[10px]"}`} href="/events/past">
  //       <Icons.past className={`${isOpen ? "w-4 h-4" : "w-6 h-6"}  shrink-0`} />
  //       <span className={`${isOpen ? "block" : "hidden"} ml-2.5 font-medium text-white text-sm xl:text-base`}>
  //         Event Lalu
  //       </span>
  //       <span className={`${isOpen ? "hidden" : "block"} mt-[10px] font-medium text-white text-[10px] xl:text-[10px] text-center`}>
  //         Past
  //       </span>
  //     </NavLink>

  //     <div className={`${isOpen ? "block" : "hidden"} pb-2 break-words text-theme-gray`}>
  //       Masuk untuk informasi
  //       <br /> lebih lengkap
  //     </div>
  //     <Button
  //       className="block w-full text-base text-center text-theme-accent border-theme-accent hover:bg-blue-500 lg:hidden"
  //       variant="outline"
  //       asChild
  //     >
  //       <Link href="/login">Masuk</Link>
  //     </Button>
  //     <Button
  //       className={`hidden w-full text-base text-theme-accent border-theme-accent hover:bg-blue-500 ${isOpen ? "lg:block" : "lg:hidden"}`}
  //       variant="outline"
  //       onClick={() => {
  //         document.getElementById("login-btn")?.click();
  //       }}
  //     >
  //       Masuk
  //     </Button>
  //     <div className="w-full h-full max-h-screen flex flex-col justify-end items-start">
  //       <div className={`${isOpen ? "block" : "hidden"} space-y-3 text-xs font-semibold xl:text-sm text-theme-gray`}>
  //         <Link href="/confirm-payment" className="block">
  //           Konfirmasi Pembayaran
  //         </Link>
  //         <Link href="/page/about" className="block">
  //           Tentang MastermindEO
  //         </Link>
  //         <Link href="/page/privacy-policy" className="block">
  //           Kebijakan Privasi
  //         </Link>
  //         <Link href="/page/terms-and-condition" className="block">
  //           Syarat & Ketentuan
  //         </Link>
  //         <Link href="/page/faq" className="block">
  //           FAQ
  //         </Link>
  //       </div>
  //       <div className={`${isOpen ? "flex flex-col" : "hidden"} pt-4 text-xs font-medium text-theme-gray`}>
  //         <p>© Copyright MastermindEO®</p>
  //         <p>All Rights Reserved.</p>
  //       </div>
  //       <div className={`${isOpen ? "flex py-8 space-x-5" : "flex flex-col items-center justify-center py-8 space-y-5"} w-full`}>
  //         <SocialIcon
  //           icon={<FacebookIcon />}
  //           href="https://www.facebook.com/mastermindeo"
  //         />
  //         <SocialIcon
  //           icon={<TwitterIcon />}
  //           href="https://www.facebook.com/mastermindeo"
  //         />
  //         <SocialIcon
  //           icon={<YoutubeIcon />}
  //           href="https://www.facebook.com/mastermindeo"
  //         />
  //         <SocialIcon
  //           icon={<InstagramIcon />}
  //           href="https://www.facebook.com/mastermindeo"
  //         />
  //       </div>
  //     </div>
  //   </>
  // );
}

export function LogoutMenuButton() {
  const { isOpen, setIsOpen } = useSidebarState();
  const auth = useAuth();

  if (auth.status !== "authenticated") return null;

  return (
    <button
      onClick={auth.logout}
      className={`${
        isOpen
          ? "flex items-center py-2.5 px-4"
          : "flex flex-col items-center justify-center py-[10px] px-[10px]"
      } bg-theme-black3 dark:bg-[#DBDFE4] text-theme-accent hover:bg-white/10 dark:hover:bg-[#788790]/10 rounded-md whitespace-nowrap w-full`}
    >
      <Icons.logout className={`${isOpen ? "w-4 h-4" : "w-6 h-6"}  shrink-0`} />

      <span
        className={`${
          isOpen ? "block" : "hidden"
        } ml-2.5 font-medium text-white dark:text-[#0D0D0D] text-sm xl:text-base`}
      >
        Logout
      </span>
      {/* <span className={`${isOpen ? "hidden" : "block"} mt-[10px] font-medium text-white dark:text-[#0D0D0D] text-[10px] xl:text-[10px] text-center`}>
        Logout
      </span> */}
    </button>
  );
}

type ReviewStarProps = {
  rating: string | number;
  reviews: string;
  participants: string;
  likes: string;
};

export function ReviewStar({
  rating,
  reviews,
  participants,
  likes,
}: ReviewStarProps) {
  return (
    <div className="w-full h-max pt-[24px] pb-4 flex items-center justify-evenly md:gap-6 gap-5 border-b border-[#374151]">
      {/* ULASAN */}
      <div className="flex flex-col">
        <div className="flex items-center justify-center gap-1">
          <h2 className="font-bold md:text-[18px] text-base leading-normal">
            {rating}
          </h2>
          <Icons.starCircle className="md:w-5 md:h-5 w-4 h-4" />
        </div>
        <p className="font-normal text-sm text-[#788790] leading-normal">
          {reviews}
        </p>
      </div>

      {/* LINE */}
      <div className="w-[1px] h-[30px] bg-[#374151]"></div>

      {/* PESERTA */}
      <div className="flex flex-col">
        <div className="flex items-center justify-center gap-1">
          <h2 className="font-bold md:text-[18px] text-base leading-normal">
            {participants}
          </h2>
        </div>
        <p className="font-normal text-sm text-[#788790] leading-normal">
          Peserta
        </p>
      </div>

      {/* LINE */}
      <div className="w-[1px] h-[30px] bg-[#374151]"></div>

      {/* SUKA */}
      <div className="flex flex-col">
        <div className="flex items-center justify-center gap-1">
          <h2 className="font-bold md:text-[18px] text-base leading-normal">
            {likes}
          </h2>
          <Icons.starLike className="md:w-5 md:h-5 w-4 h-4" />
        </div>
        <p className="font-normal text-sm text-[#788790] leading-normal">
          Menyukai
        </p>
      </div>
    </div>
  );
}
